/**
 * The Hondo bar: one place to say, type, photograph or attach anything. While typing it searches
 * the ranch and the app; on send it becomes a capture the agent files, answers or sets up. What you
 * are looking at travels with it as context ("About 42").
 *
 * Phones: the raised record button opens the bar as a sheet; holding it records right away and
 * letting go sends (slide away to cancel). Desktops: the bar sits on top of every page, ⌘K focuses
 * it, and what you sent shows in a panel under it that Esc or a click elsewhere puts away.
 * One microphone is shared by every entry point, so a recording can't start twice.
 */
import { createContext, useCallback, useContext, useEffect, useId, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent, type ReactNode } from "react";
import { Link, useNavigate } from "react-router";
import type { CaptureContext, CaptureDTO, SearchHit } from "../../contract";
import { api, qs } from "../lib/api";
import { prepareAudioUpload, useRecorder } from "../lib/audio";
import {
  HOLD_DELAY_MS, MAX_ATTACHMENTS, MIC_HINT, MIC_LABEL, STAGE_LABEL, STAGE_STEP, STAGE_TONE, canHoldToRecord, captureKind, captureStage, formatElapsed, isSettled, micMode,
  newPress, releaseAction, slidAway, stageLabel, type MicMode, type Press, type ReleaseAction, type Stage, type Staged,
} from "../lib/composer";
import { t, useLanguage } from "../lib/i18n";
import { discardSaved, flush, saveCapture, useOutbox, type OutboxItem } from "../lib/outbox";
import { invalidateAll, useApi } from "../lib/query";
import { useCan } from "../lib/session";
import { Button, Chip, Icon, IconButton, Kbd, Spinner, VisuallyHidden, cx, isSheetOpen, toast, useIsWide } from "../ui/kit";
import { ProposalCard, useCapture } from "./ProposalCard";
import { allTools, matchTools } from "./tools";

// ---------------------------------------------------------------- context from the current screen

const Ctx = createContext<{ context: CaptureContext; setContext: (c: CaptureContext) => void }>({ context: {}, setContext: () => {} });

export function CaptureContextProvider({ children }: { children: ReactNode }) {
  const [context, setContext] = useState<CaptureContext>({});
  return <Ctx.Provider value={{ context, setContext }}>{children}</Ctx.Provider>;
}

/** Pages declare what they show so "she's limping" lands on the right cow. */
export function useCaptureSubject(context: CaptureContext | null) {
  const { setContext } = useContext(Ctx);
  const key = JSON.stringify(context ?? {});
  useEffect(() => {
    setContext(context ?? {});
    return () => setContext({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
}

export const useCaptureContext = () => useContext(Ctx);

// ---------------------------------------------------------------- composer state shared by the bar and the sheet

interface ComposerState {
  /** Phones: the sheet is open. Desktops: the panel under the bar is open. */
  open: boolean;
  /** Notes sent from this browser that the bar is following, newest first. */
  tracked: string[];
  /** Text to put in the bar the next time it opens. */
  draft: string;
  autostartMic?: boolean;
  /** Bumped by useOpenComposer so the same draft can be offered twice and the bar takes focus. */
  seq?: number;
  /** On phones, what the sheet focuses first: the microphone (no keyboard pops up) or the text box. */
  focus?: "text" | "mic";
}
const ComposerCtx = createContext<{ state: ComposerState; set: (p: Partial<ComposerState>) => void }>({ state: { open: false, tracked: [], draft: "" }, set: () => {} });

export function ComposerProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ComposerState>({ open: false, tracked: [], draft: "", seq: 0 });
  const set = useCallback((p: Partial<ComposerState>) => setState((s) => ({ ...s, ...p })), []);
  const value = useMemo(() => ({ state, set }), [state, set]);
  return <ComposerCtx.Provider value={value}><EngineProvider>{children}</EngineProvider></ComposerCtx.Provider>;
}

export const useComposer = () => useContext(ComposerCtx);

/** Open the Hondo bar from anywhere, optionally with text ("Record calving for 42") or the microphone. */
export function useOpenComposer() {
  const { set, state } = useComposer();
  const seq = state.seq ?? 0;
  return (opts: { draft?: string; mic?: boolean } = {}) => set({ open: true, draft: opts.draft ?? "", autostartMic: opts.mic, seq: seq + 1, focus: opts.draft ? "text" : "mic" });
}

// ---------------------------------------------------------------- the engine: one recorder, the draft and what's being followed

type Recorder = ReturnType<typeof useRecorder>;

interface Engine {
  text: string;
  setText: (v: string) => void;
  staged: Staged[];
  addFiles: (files: FileList | File[] | null) => void;
  removeStaged: (id: string) => void;
  send: () => Promise<void>;
  recorder: Recorder;
  mode: MicMode;
  /** A hold on the phone's record button is in progress. */
  holding: { active: boolean; cancelArmed: boolean };
  setHolding: (h: { active: boolean; cancelArmed: boolean }) => void;
  /** Where each followed note is. */
  stages: Record<string, Stage>;
  untrack: (id: string) => void;
}

const EngineCtx = createContext<Engine | null>(null);
const useEngine = (): Engine => {
  const e = useContext(EngineCtx);
  if (!e) throw new Error("Composer parts must be inside ComposerProvider");
  return e;
};

function EngineProvider({ children }: { children: ReactNode }) {
  const { context } = useCaptureContext();
  const { state, set } = useComposer();
  const wide = useIsWide();
  const [text, setText] = useState("");
  const [staged, setStaged] = useState<Staged[]>([]);
  const [holding, setHolding] = useState({ active: false, cancelArmed: false });
  const [stages, setStages] = useState<Record<string, Stage>>({});
  const [announcement, setAnnouncement] = useState("");
  const outbox = useOutbox();
  const refs = useRef({ text, staged, context, state });
  refs.current = { text, staged, context, state };
  const lastStage = useRef(new Map<string, Stage>());

  useEffect(() => { if (state.draft) setText(state.draft); }, [state.draft, state.seq]);

  const track = useCallback((id: string) => {
    const tracked = refs.current.state.tracked;
    set({ tracked: [id, ...tracked.filter((x) => x !== id)].slice(0, 6), open: true, draft: "" });
  }, [set]);
  const untrack = useCallback((id: string) => set({ tracked: refs.current.state.tracked.filter((x) => x !== id) }), [set]);


  const recorder = useRecorder(async (r) => {
    const prepared = await prepareAudioUpload(r.blob, r.mime);
    if (prepared.warning) toast(prepared.warning, { tone: "warn" });
    const { text: words, staged: files, context: ctx } = refs.current;
    refs.current = { ...refs.current, text: "", staged: [] };
    setText("");
    setStaged([]);
    const saved = await saveCapture({
      kind: files.length ? "mixed" : "voice", text: words.trim() || null, context: ctx,
      files: [{ blob: prepared.blob, mime: prepared.mime, name: "voice", duration_ms: r.durationMs }, ...files.map((f) => ({ blob: f.blob, mime: f.mime, name: f.name }))],
    });
    if (!saved.durable) toast(t("Saved for now. Keep this page open until it sends."), { tone: "warn" });
    for (const f of files) if (f.url) URL.revokeObjectURL(f.url);
    track(saved.id);
  });
  const mode = micMode(recorder.supported, recorder.access, recorder.state);

  // "Say it instead" buttons open the bar with the microphone. Setup is never started for them.
  useEffect(() => {
    if (!state.autostartMic) return;
    set({ autostartMic: false });
    if ((mode === "hold" || mode === "tap") && recorder.state === "idle") void recorder.start();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.autostartMic]);

  const send = useCallback(async () => {
    const { text: value, staged: files, context: ctx } = refs.current;
    if (!value.trim() && !files.length) return;
    // Clear at once so whatever is typed next starts a new note, even before this one is saved.
    refs.current = { ...refs.current, text: "", staged: [] };
    setText("");
    setStaged([]);
    try {
      const saved = await saveCapture({ kind: captureKind(value, files), text: value.trim() || null, context: ctx, files: files.map((f) => ({ blob: f.blob, mime: f.mime, name: f.name })) });
      if (!saved.durable) toast(t("Saved for now. Keep this page open until it sends."), { tone: "warn" });
      for (const f of files) if (f.url) URL.revokeObjectURL(f.url);
      track(saved.id);
    } catch {
      setText((now) => (now ? `${value} ${now}` : value));
      setStaged((now) => [...files, ...now]);
      toast(t("Couldn't save that note. It's back in the box."), { tone: "bad" });
    }
  }, [track]);

  const addFiles = useCallback((files: FileList | File[] | null) => {
    if (!files?.length) return;
    const room = MAX_ATTACHMENTS - refs.current.staged.length;
    const list = [...files].slice(0, Math.max(0, room));
    if (files.length > list.length) toast(t("Up to {{count}} files in one note.", { count: MAX_ATTACHMENTS }), { tone: "warn" });
    setStaged((s) => [...s, ...list.map((f) => ({
      id: `${f.name}-${f.size}-${Math.random().toString(36).slice(2, 8)}`, name: f.name, mime: f.type || "application/octet-stream", size: f.size, blob: f,
      url: f.type.startsWith("image/") && typeof URL.createObjectURL === "function" ? URL.createObjectURL(f) : null,
    }))]);
  }, []);
  const removeStaged = useCallback((id: string) => setStaged((s) => {
    const gone = s.find((x) => x.id === id);
    if (gone?.url) URL.revokeObjectURL(gone.url);
    return s.filter((x) => x.id !== id);
  }), []);

  const onStage = useCallback((id: string, stage: Stage, capture: CaptureDTO | null, pending: OutboxItem | null) => {
    setStages((m) => (m[id] === stage ? m : { ...m, [id]: stage }));
    const prev = lastStage.current.get(id);
    lastStage.current.set(id, stage);
    if (prev === stage) return;
    const words = (capture?.text ?? capture?.transcript ?? pending?.text ?? (capture?.kind === "photo" || pending?.kind === "photo" ? t("Photo") : t("Voice note"))).slice(0, 80);
    setAnnouncement(t("{{status}}: {{note}}", { status: t(stageLabel(stage, wide)), note: words }));
    // When the bar is put away, still say how it went.
    if (refs.current.state.open || !prev || !isSettled(stage)) return;
    const open = { label: t("Open"), run: () => set({ open: true }) };
    if (stage === "needs_review") toast(t("Needs a look: {{note}}", { note: words }), { tone: "warn", action: open });
    else if (stage === "answered") toast(t("Hondo answered: {{note}}", { note: words }), { tone: "good", action: open });
    else if (stage === "failed") toast(t("Hondo couldn't finish this note: {{note}}", { note: words }), { tone: "bad", action: open });
    else if (stage === "filed") {
      const proposal = capture?.proposal;
      toast(t("Filed: {{note}}", { note: words }), { tone: "good", action: proposal ? { label: t("Undo"), run: () => void api.post(`/proposals/${proposal.id}/undo`).then(invalidateAll).then(() => toast(t("Undone. It's waiting for a look."))) } : open });
    }
  }, [set, wide]);

  const value: Engine = { text, setText, staged, addFiles, removeStaged, send, recorder, mode, holding, setHolding, stages, untrack };
  return (
    <EngineCtx.Provider value={value}>
      {children}
      {state.tracked.map((id) => <CaptureWatcher key={id} id={id} pending={outbox.find((o) => o.id === id) ?? null} onStage={onStage} />)}
      <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">{announcement}</div>
    </EngineCtx.Provider>
  );
}

/** Follows one sent note: polls the server while Hondo works, and reports each change of stage. */
function CaptureWatcher({ id, pending, onStage }: { id: string; pending: OutboxItem | null; onStage: (id: string, stage: Stage, capture: CaptureDTO | null, pending: OutboxItem | null) => void }) {
  const { data } = useCapture(pending ? null : id);
  const stage = captureStage(pending, data?.status);
  useEffect(() => { onStage(id, stage, data ?? null, pending); }, [id, stage, data, pending, onStage]);
  return null;
}

// ---------------------------------------------------------------- search-as-you-type

interface Suggestion { key: string; icon: string; title: string; meta?: string; to: string }

function useSuggestions(q: string): { items: Suggestion[]; loading: boolean } {
  const lang = useLanguage();
  const can = useCan();
  const [debounced, setDebounced] = useState(q);
  useEffect(() => { const h = setTimeout(() => setDebounced(q), 160); return () => clearTimeout(h); }, [q]);
  const hits = useApi<{ items: SearchHit[] }>(debounced.trim().length >= 1 ? `/search${qs({ q: debounced.trim(), limit: 8 })}` : null, { staleTime: 5000, placeholderData: (prev) => prev });
  const items = useMemo(() => {
    const needle = q.trim();
    if (!needle) return [];
    const tools = matchTools(needle, allTools(can)).slice(0, 4).map((w) => ({ key: `tool:${w.id}`, icon: w.icon, title: t(w.label), meta: t(w.area), to: w.to }));
    const records = (hits.data?.items ?? []).slice(0, 6).map((h) => ({ key: `${h.kind}:${h.id}`, icon: hitIcon(h.kind), title: h.title, meta: h.subtitle, to: h.link }));
    return [...records, ...tools];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, hits.data, lang]);
  return { items, loading: hits.isFetching };
}

export function hitIcon(kind: SearchHit["kind"]): string {
  switch (kind) {
    case "event": return "history";
    case "stock_item": return "stock";
    case "asset": return "wrench";
    case "contact": return "contact";
    case "job": return "work";
    case "fin_doc": return "receipt";
    case "task": return "task";
    default: return kind;
  }
}

// ---------------------------------------------------------------- the composer

export function Composer({ variant, onNavigate }: { variant: "bar" | "sheet"; onNavigate?: () => void }) {
  const { context, setContext } = useCaptureContext();
  const { state, set } = useComposer();
  const engine = useEngine();
  const { text, setText, staged, recorder, mode } = engine;
  const navigate = useNavigate();
  const can = useCan();
  const outbox = useOutbox();
  const listId = useId();
  const [focused, setFocused] = useState(false);
  const [active, setActive] = useState(-1);
  const input = useRef<HTMLTextAreaElement>(null);
  const root = useRef<HTMLDivElement>(null);
  const photo = useRef<HTMLInputElement>(null);
  const file = useRef<HTMLInputElement>(null);
  const { items: suggestions } = useSuggestions(text);
  const recording = recorder.state === "recording" || recorder.state === "starting" || recorder.state === "finishing";
  const showSuggestions = focused && text.trim().length > 0 && !recording;
  const rows = showSuggestions ? suggestions.length + 2 : 0; // "Tell Hondo", results, "See all"

  // ⌘K, a draft from elsewhere, or "Tell Hondo" buttons focus the bar.
  useEffect(() => {
    if (!state.seq) return;
    if (variant === "bar" || state.draft) requestAnimationFrame(() => { input.current?.focus(); const n = input.current?.value.length ?? 0; input.current?.setSelectionRange(n, n); });
  }, [state.seq, state.draft, variant]);

  // Grow the box with the text.
  useEffect(() => {
    const el = input.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  }, [text]);

  // The desktop panel steps aside on a click elsewhere, but not while one of its sheets is open.
  useEffect(() => {
    if (variant !== "bar" || !state.open) return;
    const outside = (e: MouseEvent) => {
      const target = e.target as Element | null;
      if (isSheetOpen() || target?.closest?.(".sheet-backdrop, .toaster") || root.current?.contains(target as Node)) return;
      set({ open: false });
    };
    // Esc puts the panel away from anywhere on the page (sheets and menus handle their own Esc first).
    const esc = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || e.defaultPrevented || isSheetOpen()) return;
      e.preventDefault();
      set({ open: false });
    };
    document.addEventListener("mousedown", outside);
    document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", outside); document.removeEventListener("keydown", esc); };
  }, [variant, state.open, set]);

  const go = (to: string) => { navigate(to); setText(""); setFocused(false); setActive(-1); onNavigate?.(); };
  const searchAll = () => go(`/search${qs({ q: text.trim() })}`);
  const choose = (index: number) => {
    if (index <= 0) return void submit();
    if (index === rows - 1) return searchAll();
    const s = suggestions[index - 1];
    if (s) go(s.to);
  };
  const submit = async () => { setActive(-1); await engine.send(); };

  if (!can("record") && !can("read")) return null;
  const canRecord = can("record");
  const followed = state.tracked;
  const untracked = outbox.filter((o) => !followed.includes(o.id));
  const panelOpen = variant === "bar" && state.open && !showSuggestions && (followed.length > 0 || untracked.length > 0);
  const working = followed.filter((id) => !isSettled(engine.stages[id] ?? "sending")).length;
  const activeId = active >= 0 && showSuggestions ? `${listId}-${active}` : undefined;

  return (
    <div ref={root} className={cx("composer", `composer-${variant}`, recording && "composer-recording")}>
      <div className="composer-box">
        {context.subject && (
          <div className="composer-context">
            <Chip tone="info" icon={context.subject.kind === "animal" ? "animal" : context.subject.kind === "job" ? "work" : "place"} onRemove={() => setContext({})}
              removeLabel={t("Don't attach {{subject}}", { subject: context.subject.label ?? "" })} title={t("Hondo will take this as being about {{subject}}", { subject: context.subject.label ?? "" })}>
              {t("About {{subject}}", { subject: context.subject.label ?? "" })}
            </Chip>
          </div>
        )}
        <div className="composer-row">
          <textarea
            ref={input}
            className="composer-input"
            rows={1}
            value={text}
            placeholder={canRecord ? (variant === "bar" ? t("Say or type anything: what happened, a question, or a job") : t("Type what happened, or ask a question")) : t("Search or ask a question")}
            aria-label={t("Tell Hondo")}
            role="combobox"
            aria-expanded={showSuggestions}
            aria-controls={showSuggestions ? listId : undefined}
            aria-activedescendant={activeId}
            aria-autocomplete="list"
            enterKeyHint="send"
            onFocus={() => setFocused(true)}
            onBlur={() => setTimeout(() => setFocused(false), 150)}
            onChange={(e) => { setText(e.target.value); setActive(-1); }}
            onPaste={(e) => { if (canRecord && e.clipboardData.files.length) { e.preventDefault(); engine.addFiles(e.clipboardData.files); } }}
            onKeyDown={(e) => {
              if (e.key === "ArrowDown" && showSuggestions) { e.preventDefault(); setActive((a) => Math.min(a + 1, rows - 1)); }
              else if (e.key === "ArrowUp" && showSuggestions) { e.preventDefault(); setActive((a) => Math.max(a - 1, -1)); }
              else if (e.key === "Enter" && (e.metaKey || e.ctrlKey) && text.trim()) { e.preventDefault(); searchAll(); }
              else if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
                e.preventDefault();
                if (active > 0 && showSuggestions) choose(active); else void submit();
              } else if (e.key === "Escape") {
                if (text) { e.preventDefault(); e.stopPropagation(); setText(""); setActive(-1); }
                else if (variant === "bar" && !state.open) (e.target as HTMLTextAreaElement).blur();
              }
            }}
          />
          <div className="composer-tools">
            {canRecord && !(variant === "bar" && recording) && (
              <>
                <IconButton icon="camera" label={t("Add a photo")} onClick={() => photo.current?.click()} />
                <IconButton icon="attach" label={t("Attach a file")} onClick={() => file.current?.click()} />
              </>
            )}
            {(text.trim() || staged.length > 0) && !(variant === "bar" && recording) ? (
              <button type="button" className="composer-send" onClick={() => void submit()} aria-label={t("Send")} title={t("Send")}><Icon name="send" size={20} /></button>
            ) : variant === "bar" && canRecord ? <MicButton variant="bar" /> : null}
          </div>
        </div>
        {staged.length > 0 && <StagedFiles />}
        <input ref={photo} type="file" accept="image/*" capture="environment" hidden multiple onChange={(e) => { engine.addFiles(e.target.files); e.target.value = ""; }} />
        <input ref={file} type="file" hidden multiple onChange={(e) => { engine.addFiles(e.target.files); e.target.value = ""; }} />
      </div>

      {variant === "bar" && recording && <RecordingStrip />}
      {variant === "bar" && recorder.error && <MicError />}

      {showSuggestions && (
        <ul className="composer-suggestions" role="listbox" id={listId} aria-label={t("Suggestions")}>
          <li id={`${listId}-0`} className={cx("suggestion", "suggestion-send", active <= 0 && "suggestion-active")} role="option" aria-selected={active <= 0} onMouseDown={(e) => { e.preventDefault(); void submit(); }}>
            <Icon name="sparkle" size={18} /><span className="suggestion-title">{t("Tell Hondo: “{{text}}”", { text: text.trim().slice(0, 80) })}</span><Kbd>↵</Kbd>
          </li>
          {suggestions.map((s, i) => (
            <li key={s.key} id={`${listId}-${i + 1}`} className={cx("suggestion", i + 1 === active && "suggestion-active")} role="option" aria-selected={i + 1 === active}
              onMouseDown={(e) => { e.preventDefault(); go(s.to); }} onMouseEnter={() => setActive(i + 1)}>
              <Icon name={s.icon} size={18} /><span className="suggestion-title">{s.title}</span>{s.meta && <span className="suggestion-meta">{s.meta}</span>}
            </li>
          ))}
          <li id={`${listId}-${rows - 1}`} className={cx("suggestion", "suggestion-all", active === rows - 1 && "suggestion-active")} role="option" aria-selected={active === rows - 1}
            onMouseDown={(e) => { e.preventDefault(); searchAll(); }} onMouseEnter={() => setActive(rows - 1)}>
            <Icon name="search" size={18} /><span className="suggestion-title">{t("See all results for “{{text}}”", { text: text.trim().slice(0, 60) })}</span>{variant === "bar" && <Kbd>⌘↵</Kbd>}
          </li>
        </ul>
      )}

      {variant === "bar" && !panelOpen && !showSuggestions && followed.length > 0 && (
        <button type="button" className="composer-pill" onClick={() => set({ open: true })} aria-label={t("Show your notes")}>
          {working > 0 ? <Icon name="spinner" size={14} className="spin" /> : <Icon name="done" size={14} />}
          {working > 0 ? plural(working, "{{count}} note in progress", "{{count}} notes in progress") : plural(followed.length, "{{count}} recent note", "{{count}} recent notes")}
        </button>
      )}

      {panelOpen && (
        <section className="composer-panel" aria-label={t("Your notes")}>
          <header className="composer-panel-head">
            <h2>{t("Your notes")}</h2>
            <Link to="/review" className="link" onClick={() => set({ open: false })}>{t("Needs a look")}</Link>
            <IconButton icon="close" label={t("Close")} onClick={() => set({ open: false })} />
          </header>
          <NoteList followed={followed} untracked={untracked} />
          <p className="composer-panel-foot"><Kbd>{"Esc"}</Kbd> {t("puts this away. Your notes keep going.")}</p>
        </section>
      )}

      {variant === "sheet" && (
        <>
          {canRecord && <MicBlock />}
          <div className="composer-results">
            <NoteList followed={followed} untracked={untracked} />
            {followed.length === 0 && untracked.length === 0 && !showSuggestions && <ComposerHints />}
          </div>
        </>
      )}
    </div>
  );
}

function plural(count: number, one: string, other: string) { return t(count === 1 ? one : other, { count }); }

function StagedFiles() {
  const { staged, removeStaged } = useEngine();
  return (
    <ul className="staged" aria-label={t("Attached, not sent yet")}>
      {staged.map((s) => (
        <li key={s.id} className="staged-item">
          {s.url ? <img src={s.url} alt={t("Photo to send: {{name}}", { name: s.name })} /> : <span className="staged-file"><Icon name="file" size={18} /><span>{s.name}</span></span>}
          <button type="button" className="staged-x" aria-label={t("Remove {{name}}", { name: s.name })} onClick={() => removeStaged(s.id)}><Icon name="close" size={14} /></button>
        </li>
      ))}
    </ul>
  );
}

function NoteList({ followed, untracked }: { followed: string[]; untracked: OutboxItem[] }) {
  const outbox = useOutbox();
  const { stages, untrack } = useEngine();
  const wide = useIsWide();
  return (
    <div className="note-list">
      {untracked.map((o) => <SavedNote key={o.id} item={o} />)}
      {followed.map((id) => <FollowedNote key={id} id={id} pending={outbox.find((o) => o.id === id) ?? null} stage={stages[id] ?? "sending"} wide={wide} onDone={() => untrack(id)} />)}
    </div>
  );
}

/** A note saved in this browser from before, still waiting to send. */
function SavedNote({ item }: { item: OutboxItem }) {
  const wide = useIsWide();
  const stage = captureStage(item, null);
  return (
    <div className="outbox-note">
      <Icon name={item.state === "sending" ? "upload" : "offline"} size={16} />
      <span className="outbox-text">{item.text ?? (item.kind === "voice" ? t("Voice note") : item.kind === "photo" ? t("Photo") : t("File"))}</span>
      <Chip tone={STAGE_TONE[stage]}>{t(stageLabel(stage, wide))}</Chip>
      {item.state !== "sending" && <IconButton icon="delete" label={t("Discard this note")} onClick={() => void discardSaved(item.id)} />}
    </div>
  );
}

/** One note sent from here: its progress until Hondo is done, then the proposal itself. */
function FollowedNote({ id, pending, stage, wide, onDone }: { id: string; pending: OutboxItem | null; stage: Stage; wide: boolean; onDone: () => void }) {
  // The watcher polls; this reads the same cache.
  const { data } = useApi<CaptureDTO>(pending ? null : `/captures/${id}`);
  if (data && isSettled(stage)) return <ProposalCard capture={data} onDone={onDone} compact />;
  const words = pending?.text ?? data?.text ?? data?.transcript;
  const kind = pending?.kind ?? data?.kind;
  return (
    <article className="proposal proposal-pending" aria-label={t(stageLabel(stage, wide))}>
      <StageSteps stage={stage} wide={wide} />
      {words ? <blockquote className="proposal-quote">{words}</blockquote> : <p className="muted">{kind === "voice" || kind === "mixed" ? t("Voice note") : kind === "photo" ? t("Photo") : t("File")}</p>}
      {pending?.state === "failed" && pending.error && (
        <p className="proposal-working"><Icon name="offline" size={18} /><span>{pending.error}. {t("It will send by itself when it can.")}</span>
          <Button size="sm" variant="ghost" icon="reset" onClick={() => void flush()}>{t("Try now")}</Button></p>
      )}
    </article>
  );
}

/** Saved on this phone → Sending → Working on it → the result, as four small steps. */
export function StageSteps({ stage, wide }: { stage: Stage; wide: boolean }) {
  const at = STAGE_STEP[stage];
  const steps: Array<{ step: number; label: string }> = [
    { step: 0, label: stageLabel("saved", wide) }, { step: 1, label: STAGE_LABEL.sending }, { step: 2, label: STAGE_LABEL.working }, { step: 3, label: at === 3 ? STAGE_LABEL[stage] : "Done" },
  ];
  return (
    <div className={cx("stage-steps", `tone-${STAGE_TONE[stage]}`)}>
      <ol aria-hidden>{steps.map((s) => <li key={s.step} className={cx(s.step < at && "is-done", s.step === at && "is-current")} />)}</ol>
      <span className="stage-label">{stage === "working" || stage === "sending" ? <Icon name="spinner" size={14} className="spin" /> : null}{t(steps[at].label)}</span>
    </div>
  );
}

// ---------------------------------------------------------------- the microphone

function useMicPress(variant: "bar" | "sheet") {
  const { recorder, mode } = useEngine();
  const press = useRef<Press | null>(null);
  const pointerHandled = useRef(false);
  const [cancelArmed, setCancelArmed] = useState(false);
  const [pressed, setPressed] = useState(false);
  const act = (action: ReleaseAction) => {
    if (action === "send") recorder.stop();
    else if (action === "cancel") { recorder.cancel(); toast(t("Cancelled. Nothing was saved.")); }
    else if (action === "too-early") recorder.cancel(t("The microphone wasn't ready yet. Tap once to start, then talk when the timer shows."));
  };
  return {
    pressed, cancelArmed,
    props: {
      onPointerDown: (e: ReactPointerEvent<HTMLButtonElement>) => {
        pointerHandled.current = false;
        if (!canHoldToRecord(mode) && mode !== "recording") return;
        if (e.button !== 0 && e.pointerType === "mouse") return;
        pointerHandled.current = true;
        e.preventDefault();
        try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* older browsers */ }
        press.current = newPress(Date.now(), e.clientX, e.clientY, mode === "recording");
        setPressed(true);
        if (!press.current.wasRecording && recorder.state === "idle") { press.current.started = true; void recorder.start(); }
      },
      onPointerMove: (e: ReactPointerEvent<HTMLButtonElement>) => {
        const p = press.current;
        if (!p?.started) return;
        const armed = slidAway(p, e.clientX, e.clientY);
        if (armed !== p.cancelArmed) { p.cancelArmed = armed; setCancelArmed(armed); }
      },
      onPointerUp: () => {
        const p = press.current;
        press.current = null;
        setPressed(false);
        setCancelArmed(false);
        if (p) act(releaseAction(p, Date.now(), recorder.state));
      },
      onPointerCancel: () => {
        const p = press.current;
        press.current = null;
        setPressed(false);
        setCancelArmed(false);
        if (!p) return;
        if (recorder.state === "starting") recorder.cancel();
        else if (recorder.state === "recording" && Date.now() - p.at > 650) recorder.stop();
      },
      onClick: (e: React.MouseEvent<HTMLButtonElement>) => {
        // One gesture never opens the microphone twice: pointer presses were handled above.
        if (e.detail > 0 && pointerHandled.current) { pointerHandled.current = false; return; }
        if (mode === "enable" || mode === "blocked" || mode === "unavailable") return void recorder.requestAccess();
        if (mode === "recording") return recorder.stop();
        if (mode === "hold" || mode === "tap") return void recorder.start();
      },
      onKeyDown: (e: React.KeyboardEvent<HTMLButtonElement>) => { if (e.repeat && (e.key === " " || e.key === "Enter")) e.preventDefault(); },
      onContextMenu: (e: React.MouseEvent) => e.preventDefault(),
      disabled: mode === "unsupported" || mode === "checking" || mode === "requesting" || mode === "finishing",
      "aria-pressed": mode === "recording" || mode === "hold" || mode === "tap" ? mode === "recording" : undefined,
      "aria-label": mode === "recording" ? t("Stop and send") : mode === "hold" || mode === "tap" ? t("Start recording") : t(MIC_LABEL[mode]),
      "data-variant": variant,
    },
  };
}

function MicButton({ variant }: { variant: "bar" | "sheet" }) {
  const { mode } = useEngine();
  const { props } = useMicPress(variant);
  const recording = mode === "recording";
  return (
    <button type="button" className={cx("composer-mic", recording && "is-recording", (mode === "blocked" || mode === "unavailable") && "is-off")} title={t(MIC_HINT[mode])} {...props}>
      <Icon name={recording ? "stop" : mode === "blocked" || mode === "unavailable" || mode === "unsupported" ? "micOff" : mode === "starting" || mode === "finishing" || mode === "checking" ? "spinner" : "mic"} size={22}
        className={mode === "starting" || mode === "finishing" || mode === "checking" ? "spin" : undefined} />
    </button>
  );
}

/** The sheet's big microphone with its label, live meter and plain guidance. */
function MicBlock() {
  const { recorder, mode, holding } = useEngine();
  const { props, pressed, cancelArmed } = useMicPress("sheet");
  const hintId = useId();
  const recording = mode === "recording";
  const armed = cancelArmed || holding.cancelArmed;
  const held = pressed || holding.active;
  const label = recording ? (armed ? t("Let go to cancel") : held ? t("Let go to send") : t("Tap to send")) : t(MIC_LABEL[mode]);
  const hint = recording ? (held ? t("Slide away to cancel.") : t(MIC_HINT.recording)) : t(MIC_HINT[mode]);
  return (
    <div className={cx("mic-block", recording && "is-recording", armed && "is-cancel")}>
      <button type="button" className={cx("mic-main", recording && "is-recording", (mode === "blocked" || mode === "unavailable") && "is-off")} aria-describedby={hintId} {...props}>
        {recording ? <><Icon name="stop" size={26} /><span className="mic-time">{formatElapsed(recorder.elapsedMs)}</span></>
          : <Icon name={mode === "blocked" || mode === "unavailable" || mode === "unsupported" ? "micOff" : mode === "starting" || mode === "finishing" || mode === "checking" ? "spinner" : "mic"} size={34}
            className={mode === "starting" || mode === "finishing" || mode === "checking" ? "spin" : undefined} />}
      </button>
      {recording && <span className="meter mic-meter" aria-hidden>{recorder.levels.map((l, i) => <span key={i} style={{ height: `${Math.max(8, l * 100)}%` }} />)}</span>}
      <p className="mic-label" aria-live="polite">{label}</p>
      <p className="mic-hint" id={hintId}>{hint}</p>
      {recording && !held && <Button size="sm" variant="ghost" icon="close" onClick={() => { recorder.cancel(); toast(t("Cancelled. Nothing was saved.")); }}>{t("Cancel")}</Button>}
      {recorder.error && <MicError />}
    </div>
  );
}

function RecordingStrip() {
  const { recorder } = useEngine();
  return (
    <div className="composer-meter" role="status">
      <span className="rec-dot" aria-hidden />
      <span className="rec-time">{formatElapsed(recorder.elapsedMs)}</span>
      <span className="meter" aria-hidden>{recorder.levels.map((l, i) => <span key={i} style={{ height: `${Math.max(8, l * 100)}%` }} />)}</span>
      <span className="rec-hint">{recorder.state === "starting" ? t("Getting ready…") : t("Listening. Press stop to send.")}</span>
      <Button size="sm" variant="ghost" onClick={() => { recorder.cancel(); toast(t("Cancelled. Nothing was saved.")); }}>{t("Cancel")}</Button>
      <MicButton variant="bar" />
    </div>
  );
}

function MicError() {
  const { recorder } = useEngine();
  return (
    <div className="composer-error" role="alert">
      <Icon name="warn" size={16} />
      <span>{recorder.error}</span>
      <IconButton icon="close" label={t("Dismiss")} onClick={recorder.clearError} size={16} />
    </div>
  );
}

// ---------------------------------------------------------------- the phone's raised record button

/**
 * Tap: open the Hondo bar. Hold (with the microphone already allowed): record right away, let go to
 * send, slide away to cancel. Without access, a hold just opens the bar where "Enable microphone" is
 * its own tap, so a permission prompt never interrupts a hold.
 */
export function RecordTabButton() {
  const { recorder, mode, setHolding } = useEngine();
  const { set, state } = useComposer();
  const press = useRef<Press | null>(null);
  const timer = useRef(0);
  const pointerHandled = useRef(false);
  const hintId = useId();
  const recording = mode === "recording" || mode === "starting";
  useEffect(() => () => window.clearTimeout(timer.current), []);
  const open = () => set({ open: true, seq: (state.seq ?? 0) + 1, draft: "", focus: "mic" });
  const end = (cancelled: boolean) => {
    window.clearTimeout(timer.current);
    const p = press.current;
    press.current = null;
    setHolding({ active: false, cancelArmed: false });
    if (!p) return;
    if (cancelled) {
      if (!p.started) return;
      if (recorder.state === "starting") recorder.cancel();
      else if (recorder.state === "recording") recorder.stop();
      return;
    }
    const action = releaseAction(p, Date.now(), recorder.state, { tapOpens: true });
    if (action === "open") open();
    else if (action === "send") recorder.stop();
    else if (action === "cancel") { recorder.cancel(); toast(t("Cancelled. Nothing was saved.")); }
    else if (action === "too-early") recorder.cancel(t("The microphone wasn't ready yet. Hold the button and wait for the timer before you talk."));
  };
  return (
    <button type="button" className={cx("tab-record", recording && "is-recording")} aria-label={recording ? t("Recording. Let go to send.") : t("Record or ask")} aria-describedby={hintId}
      onPointerDown={(e) => {
        pointerHandled.current = false;
        if (e.button !== 0 && e.pointerType === "mouse") return;
        pointerHandled.current = true;
        try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* older browsers */ }
        press.current = newPress(Date.now(), e.clientX, e.clientY, recorder.state === "recording");
        if (press.current.wasRecording || !canHoldToRecord(mode)) return;
        timer.current = window.setTimeout(() => {
          const p = press.current;
          if (!p) return;
          p.started = true;
          setHolding({ active: true, cancelArmed: false });
          set({ open: true, focus: "mic" });
          try { navigator.vibrate?.(12); } catch { /* no haptics */ }
          void recorder.start();
        }, HOLD_DELAY_MS);
      }}
      onPointerMove={(e) => {
        const p = press.current;
        if (!p?.started) return;
        const armed = slidAway(p, e.clientX, e.clientY);
        if (armed !== p.cancelArmed) { p.cancelArmed = armed; setHolding({ active: true, cancelArmed: armed }); }
      }}
      onPointerUp={() => end(false)}
      onPointerCancel={() => end(true)}
      onClick={(e) => {
        if (e.detail > 0 && pointerHandled.current) { pointerHandled.current = false; return; }
        open();
      }}
      onContextMenu={(e) => e.preventDefault()}>
      <span className="tab-record-btn"><Icon name={recording ? "stop" : "mic"} size={26} /></span>
      <VisuallyHidden id={hintId}>{canHoldToRecord(mode) ? t("Tap to open. Hold to talk, let go to send.") : t("Tap to open the Hondo bar.")}</VisuallyHidden>
    </button>
  );
}

/** The shared microphone, for the shell (closing the sheet mid-recording sends what was said). */
export function useComposerMic() {
  const { mode, recorder, holding } = useEngine();
  return { mode, holding, stop: recorder.stop, cancel: recorder.cancel };
}

/** A small "Working on it" marker for the phone's top bar while a note is on its way. */
export function ComposerStatus() {
  const { state, set } = useComposer();
  const { stages } = useEngine();
  const working = state.tracked.filter((id) => !isSettled(stages[id] ?? "sending"));
  const waiting = state.tracked.filter((id) => stages[id] === "needs_review");
  if (!working.length && !waiting.length) return null;
  const label = working.length ? t(STAGE_LABEL.working) : t(STAGE_LABEL.needs_review);
  return (
    <button type="button" className={cx("status-pill", working.length ? "tone-info" : "tone-warn")} onClick={() => set({ open: true, focus: "mic" })} aria-label={label} title={label}>
      {working.length ? <Icon name="spinner" size={14} className="spin" /> : <Icon name="warn" size={14} />}
      <span aria-hidden>{label}</span>
    </button>
  );
}

function ComposerHints() {
  const examples = [
    t("42 calved this morning, heifer calf, tagged 42P"),
    t("Gave the replacement heifers their Vision 7 booster"),
    t("Moved the spring calvers to North Pasture"),
    t("How many open cows do we have?"),
    t("Start preg checks on the fall calvers next Tuesday"),
  ];
  const { setText } = useEngine();
  return (
    <div className="composer-hints">
      <p className="hint-title">{t("Talk to Hondo like you'd tell a good ranch hand.")}</p>
      <ul>{examples.map((e) => <li key={e}><button type="button" onClick={() => setText(e)}>“{e}”</button></li>)}</ul>
    </div>
  );
}

/** Recent captures for the person, newest first. Used on Today and in the sheet. */
export function useRecentCaptures(limit = 10) {
  return useApi<{ items: CaptureDTO[] }>(`/captures${qs({ mine: 1, limit })}`);
}

export { Button, ProposalCard, Spinner };
export const fetchCapture = (id: string) => api.get<CaptureDTO>(`/captures/${id}`);
